import React, { useEffect, useState } from "react";
import { customerApi } from "@/lib/customerApi";
import { PerfCharts, PerfTable, PerfTimeline } from "./PerfCharts";
import { gbp } from "@/lib/customerApi";
import { money } from "./PerfCharts";

const PERIODS = [["since", "Since you subscribed"], ["today", "Today"], ["week", "This week"], ["month", "This month"]];

function Pills({ items, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1 rounded-xl border border-blue-900/50 bg-[#050914] p-1">
      {items.map(([id, label]) => (
        <button key={id} onClick={() => onChange(id)} data-testid={`${testid}-${id}`}
          className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${value === id ? "bg-[#38BDF8] text-[#050914]" : "text-[#94A3B8] hover:text-white"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function BalanceCard({ data }) {
  const b = (data.balances || [])[0];
  if (!b || b.balance == null) return null;
  const change = b.start_balance != null ? b.balance - b.start_balance : null;
  const started = b.started_at ? new Date(b.started_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <div data-testid="perf-balance-card" className="mt-6 rounded-xl border border-blue-900/50 bg-[#050914] p-4">
      <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Account balance · MT5 {b.account}</div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
        {b.start_balance != null && <span data-testid="perf-balance-start" className="text-lg text-[#94A3B8]">{money(b.start_balance, b.currency)}</span>}
        {b.start_balance != null && <span className="text-[#64748B]">→</span>}
        <span data-testid="perf-balance-now" className="text-2xl text-white">{money(b.balance, b.currency)}</span>
        {change != null && <span data-testid="perf-balance-change" className={`text-sm ${change >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`}>({change >= 0 ? "+" : ""}{money(change, b.currency)})</span>}
      </div>
      <div className="text-[11px] text-[#94A3B8] mt-1">
        {started && `Starting balance from ${started}, when your first BotGenomix bot reported. `}Equity now {money(b.equity, b.currency)}.
        Deposits and withdrawals also change your balance; the bot figures below count only trades your BotGenomix bots made.
      </div>
    </div>
  );
}

function FeesCompare({ data, fees }) {
  const since = fees?.since ? new Date(fees.since * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
  return (
    <div data-testid="perf-fees-compare" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="rounded-xl border border-blue-900/50 bg-[#050914] p-4">
        <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">All your BotGenomix bots combined{since ? ` · subscribed ${since}` : ""}</div>
        <div data-testid="perf-portfolio-net" className={`text-2xl mt-1 ${data.total_net >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`}>{money(data.total_net, data.currency)}</div>
        <div className="text-[11px] text-[#94A3B8] mt-1">Net of swap and commission, in your account currency, for the period selected</div>
      </div>
      <div className="rounded-xl border border-blue-900/50 bg-[#050914] p-4">
        <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Subscriptions paid to BotGenomix (all time)</div>
        <div data-testid="perf-fees-paid" className="text-2xl mt-1 text-white">{fees ? gbp(fees.paid_gbp, 2) : "…"}</div>
        <div className="text-[11px] text-[#94A3B8] mt-1">{fees?.invoices ?? 0} payment{fees?.invoices === 1 ? "" : "s"} in GBP - convert at your own rate if your account isn't in GBP</div>
      </div>
    </div>
  );
}

export default function PerformanceSection({ me }) {
  const [period, setPeriod] = useState("since");
  const [fees, setFees] = useState(null);
  const [mode, setMode] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { customerApi.get("/store/account/fees").then((r) => setFees(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    customerApi.get("/store/account/performance", { params: { period, mode } }).then((r) => { setData(r.data); setErr(false); }).catch(() => setErr(true));
  }, [period, mode]);
  if (!me.picks?.length) return null;
  const empty = data && !data.reporting;
  return (
    <section data-testid="performance-section" className="border border-blue-900/40 bg-[#020617]/70 rounded-xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#34D399]">Your personal dashboard</div>
          <h2 className="text-2xl font-medium mt-1">Your BotGenomix bots</h2>
          <div className="text-xs text-[#94A3B8] mt-1 max-w-xl">Only trades placed by your BotGenomix bots, reported by the software in your own MT5 terminal. Other trades on your account aren't collected. This is your own data for your records - it isn't advice.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Pills items={[["live", "Live account"], ["demo", "Demo account"]]} value={data?.mode} onChange={setMode} testid="perf-mode" />
          <Pills items={PERIODS} value={period} onChange={setPeriod} testid="perf-period" />
        </div>
      </div>
      {err && !data ? <div data-testid="perf-error" className="text-sm text-[#F87171] mt-6">Couldn't load your results - refresh the page to try again.</div>
      : !data ? <div className="mono text-xs text-[#64748B] mt-6">Loading…</div> : empty ? (
        <div data-testid="perf-empty" className="mt-6 rounded-xl border border-dashed border-blue-900/60 p-6 text-sm text-[#94A3B8]">
          No results yet on your {data.mode} account. Once your bots are running in MT5 they report here automatically.
          If you downloaded a bot before this feature launched, download it again and re-attach it to the chart.
        </div>
      ) : (
        <>
          <BalanceCard data={data} />
          <FeesCompare data={data} fees={fees} />
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#38BDF8] mt-8" data-testid="store-bots-heading">Each bot separately</div>
          <PerfCharts data={data} />
          <PerfTimeline data={data} />
          <PerfTable data={data} />
        </>
      )}
    </section>
  );
}
